import moment from "moment";
import { ProductFilterDay } from "../../OrderFlowProvider/OrderFlowTypes";
import { NONE_INGREDIENT_ID } from "../RecipeSelection/recipeSelectionUtils";
/**
 *  Helper function to generate RecipeChanges for automatic and customized replacements.
 *  Automatic changes and skips only apply when RecipeIngredientReplacementMode is enabled
 *
 * @param autoChanges
 * @param customChanges
 */
const generateRecipeChanges = (shouldModifyRecipes, autoChanges, customChanges, skips) => {
    let recipeChanges = [];
    if (autoChanges && shouldModifyRecipes) {
        autoChanges.forEach((change) => recipeChanges.push({
            // The API expects the None ingredient to have an id of 0
            IngredientReplacementId: change.ReplacementIngredient.id === NONE_INGREDIENT_ID ? 0 : change.ReplacementIngredient.id,
            IngredientOriginalId: change.OriginalIngredient.id,
            RecipeIngredientId: change.RecipeIngredientId,
            MustBeReplaced: change.MustBeReplaced,
            ReplacementPrice: 0,
            Unit: change.Unit,
            ReplacementQty: change.ReplacementQty,
        }));
    }
    if (customChanges) {
        customChanges.forEach((option) => {
            option.selectedOptions.forEach((selectedOption) => {
                const change = {
                    IngredientOriginalId: option.originalIngredientId,
                    RecipeIngredientId: option.recipeIngredientId,
                    MustBeReplaced: option.mustBeReplaced,
                    IngredientSkip: false,
                    // The API expects the None ingredient to have an id of 0
                    IngredientReplacementId: selectedOption.replacementIngredientId === NONE_INGREDIENT_ID ? 0 : selectedOption.replacementIngredientId,
                    ReplacementPrice: selectedOption.replacementPrice,
                    Unit: selectedOption.Unit,
                    ReplacementQty: selectedOption.ReplacementQty
                };
                recipeChanges.push(change);
            });
        });
    }
    if (skips && shouldModifyRecipes) {
        skips.forEach((skip) => recipeChanges.push({
            IngredientOriginalId: skip.OriginalIngredientId,
            RecipeIngredientId: skip.RecipeIngredientId,
            IngredientSkip: true,
            MustBeReplaced: skip.MustBeReplaced,
            Unit: undefined,
            ReplacementQty: 0
        }));
    }
    return removeDuplicatesFromArray(recipeChanges);
};
/**
 * @param meal
 * @param assignment
 * @param quantity
 * @returns Helper function to set quantity, price and recipe changes for a meal
 */
export const mealToCheckoutConverter = (meal, shouldModifyRecipes, assignment, quantity) => {
    if (!quantity) {
        quantity = meal.Quantity;
    }
    const recipeChanges = generateRecipeChanges(shouldModifyRecipes, assignment === null || assignment === void 0 ? void 0 : assignment.AutomaticReplacements, assignment === null || assignment === void 0 ? void 0 : assignment.SelectedIngredientOptions, assignment === null || assignment === void 0 ? void 0 : assignment.Skips);
    let newMeal = {
        MealId: meal.ProgramMealId,
        MealName: meal.ProgramMealName,
        Qty: quantity,
        Price: meal.Price,
        MealDate: moment(meal.MealDate).format("MM/DD/YYYY"),
        DeliveryDate: moment(meal.DeliveryDate).format("MM/DD/YYYY"),
        RecipeId: assignment && assignment.RecipeId,
        RecipeName: assignment && assignment.RecipeName,
        RecipeChanges: recipeChanges,
        MealTypeId: meal.MealTypeId
    };
    return newMeal;
};
/**
 * Looks at meals and products to get the delivery dates for the order. If there are no meals or products use the start date.
 *
 * @param meals
 * @param products
 * @param deliveryDays
 * @returns
 */
export const getDeliveryDatesForOrder = (meals, products, deliveryDays, startDateStr) => {
    let days = new Set();
    const startDate = moment(startDateStr);
    if (!deliveryDays)
        return Array.from(days);
    if (startDate.isValid()) {
        // Start Date should always be set as delivery day
        days.add(startDate.format("l"));
    }
    if (meals.length) {
        meals.forEach((meal) => {
            var _a;
            const day = (_a = deliveryDays.find((dd) => moment(dd.MealDateString).isSame(meal.MealDate, "date"))) === null || _a === void 0 ? void 0 : _a.ShipDateString;
            if (day) {
                days.add(day);
            }
        });
    }
    if (products.length) {
        products.forEach((p) => {
            var _a;
            let day = (_a = deliveryDays.find((dd) => {
                const mealDate = moment(dd.MealDateString);
                return mealDate.day() === p.DayOfWeek && mealDate >= startDate;
            })) === null || _a === void 0 ? void 0 : _a.ShipDateString;
            // API will assign firstAvailable products to earliest delivery day; add a delivery day only if we didn't already add one
            // since we prefer that product is delivered with meals
            if (day === undefined && p.DayOfWeek === ProductFilterDay.firstAvailable && !days.size) {
                day = deliveryDays.filter((dd) => moment(dd.MealDateString) >= startDate)[0].MealDateString;
            }
            if (day) {
                days.add(day);
            }
        });
    }
    const deliveryDatesForOrder = Array.from(days);
    const sortedDeliveryDatesForOrder = deliveryDatesForOrder.sort((a, b) => moment(a).diff(b));
    return sortedDeliveryDatesForOrder;
};
export const removeDuplicatesFromArray = (array) => {
    const seen = new Set();
    return array.filter(item => {
        const serializedItem = JSON.stringify(item);
        if (seen.has(serializedItem)) {
            return false;
        }
        else {
            seen.add(serializedItem);
            return true;
        }
    });
};
/**
 * Get the products that are available for the order period based on the start date.
 *
 * @param startDay
 * @param deliveryDays
 * @param cartProducts
 * @param modelProducts
 * @returns
 */
export const getProductsForOrderPeriod = (startDay, deliveryDays, cartProducts, modelProducts) => {
    const isStartDateFirstDay = deliveryDays[0] === startDay;
    const productsToDisplay = cartProducts === null || cartProducts === void 0 ? void 0 : cartProducts.filter((p) => p.quantity > 0).map((product) => {
        //Products in the model have the original dayOfWeek value
        //It gets set to -1 when Product module is set to FirstAvailableDay
        const modelProduct = modelProducts.find((p) => p.productId === product.productId);
        //If the day of week is -1 we need to check if its original day of week is in the order period
        const dayOfWeek = product.DayOfWeek === -1 ? modelProduct === null || modelProduct === void 0 ? void 0 : modelProduct.DayOfWeek : product.DayOfWeek;
        const productIsOnOrAfterStartDay = dayOfWeek >= moment(startDay).day();
        return Object.assign(Object.assign({}, product), { 
            // If the startDate is the first day of the order period all products in the order period are valid
            // If the startDate is not the first day of the order period only products on or after that day are valid
            isOnShortWeek: isStartDateFirstDay
                ? !deliveryDays.includes(dayOfWeek !== null && dayOfWeek !== void 0 ? dayOfWeek : -1)
                : !productIsOnOrAfterStartDay });
    });
    return productsToDisplay;
};
